import { useGetFormDetail2 } from "@/queries/formaly-request";
import type { FlowExecutionType } from "@/types/entities/flows";
import {
  FORM_ASSIGNMENTS_STATUS,
  type FormDetailResponse,
} from "@/types/entities/formality-request";
import { formatDateTime } from "@/utils/dates";
import { cn } from "@/utils/theme";
import { Badge, Button, Icon } from "@elaniintc/goes-design-system";
import { useState } from "react";
import { getExecutionBadge } from ".";
import { useGetTableData } from "@/queries/data-center/data-center.api";

interface Props {
  execution: FlowExecutionType["node_executions"][number];
  formId: string;
  formalityId: string;
}

const FormSimpleContent = ({ execution, formId, formalityId }: Props) => {
  const { data, isFetching } = useGetFormDetail2(formId, formalityId);

  if (isFetching) {
    return (
      <div className="w-full h-full flex items-center justify-center">
        <Icon name="spinner-third" type="fas" className="animate-spin" />
      </div>
    );
  }

  const assigment = data?.form_assignments[0];

  return (
    <div className="w-full flex flex-col gap-4">
      <div className="flex flex-col gap-1">
        <p>Estado del nodo: {getExecutionBadge(execution.status)}</p>
        <p>
          Estado actual en el flujo:{" "}
          <Badge color={statusColor(assigment?.status || "")} size="sm">
            {getAssigmentStatusLabel(
              assigment?.status as keyof typeof FORM_ASSIGNMENTS_STATUS,
            ) || "No disponible"}
          </Badge>
        </p>
        <p>
          Llenado:{" "}
          {assigment?.assigned_at
            ? formatDateTime(assigment.assigned_at)
            : "No disponible"}
        </p>
      </div>
      {data?.steps.map((step) => {
        return <Step step={step} key={step.id} />;
      })}
    </div>
  );
};

export default FormSimpleContent;

const Step = ({ step }: { step: FormDetailResponse["steps"][number] }) => {
  const [isOpen, setIsOpen] = useState(true);
  return (
    <div
      key={step.id}
      className="border border-neutral rounded-lg p-4 flex flex-col gap-2"
    >
      <div className="flex justify-between items-center">
        <p>{step.name}</p>
        <Button
          aria-label={isOpen ? "Cerrar" : "Abrir"}
          size="icon"
          variant="tertiary"
          onClick={() => setIsOpen(!isOpen)}
        >
          <Icon type="far" name={isOpen ? "chevron-up" : "chevron-down"} />
        </Button>
      </div>
      <div className={cn(!isOpen && "hidden")}>
        {step.sections.map((section) => {
          return <Section section={section} key={section.id} />;
        })}
      </div>
    </div>
  );
};

const Section = ({
  section,
}: {
  section: FormDetailResponse["steps"][number]["sections"][number];
}) => {
  const [isOpen, setIsOpen] = useState(true);
  return (
    <div className="border border-neutral rounded-lg p-4 bg-neutral-object">
      <div className="flex justify-between items-center ">
        <h3>{section.title}</h3>
        <Button
          aria-label={isOpen ? "Cerrar" : "Abrir"}
          size="icon"
          variant="tertiary"
          onClick={() => setIsOpen(!isOpen)}
        >
          <Icon type="far" name={isOpen ? "chevron-up" : "chevron-down"} />
        </Button>
      </div>
      <div className={cn("px-4 flex flex-col gap-4", !isOpen && "hidden")}>
        {section.fields.map((field) => {
          return (
            <div key={field.id} className="flex gap-4">
              <p className="w-1/4">{field.label}: </p>
              <FilledFieldValue
                value={field.filled_fields?.[0]?.value || ""}
                type={field.type}
                config={field.config}
                files={field.filled_fields?.[0]?.files || []}
              />
            </div>
          );
        })}
      </div>
    </div>
  );
};

const FilledFieldValue = ({
  value,
  type,
  config,
  files,
}: {
  value:
    | string
    | number
    | string[]
    | {
        start: string;
        end: string;
      };
  type: string;
  config: {
    multiple: boolean;
    options_type: "database" | "manual" | string;
    options?: {
      label: string;
      value: string | number;
    }[];
    data_center_table: string;
    data_center_column: string;
    source: string;
  };
  files: {
    file_name: string;
    url: string;
    size_formatted: string;
  }[];
}) => {
  if (type === "file") {
    return (
      <ul className="flex gap-2 flex-wrap">
        {files.map((f) => {
          return (
            <Badge
              variant="neutral"
              className="max-w-70 cursor-pointer"
              onClick={() => {
                window.open(f.url, "_blank", "noopener,noreferrer");
              }}
            >
              <span className="truncate">{f.file_name}</span>
              <Icon type="fas" name="file" size="xs" className="shrink-0" />
            </Badge>
          );
        })}
      </ul>
    );
  }

  if (type === "select" && config.options_type === "database") {
    return (
      <DatabaseFieldValue
        value={value as string | number}
        table_name={config.data_center_table}
        table_source={config.source}
        column_name={config.data_center_column}
      />
    );
  }

  if (
    type === "select" &&
    config.options_type === "manual" &&
    !config.multiple
  ) {
    const value2: undefined | string = config.options?.find(
      (o) => o.value === value,
    )?.label;
    return <span>{value2 || ""}</span>;
  }

  if (
    type === "select" &&
    config.options_type === "manual" &&
    config.multiple &&
    Array.isArray(value)
  ) {
    return (
      <ul>
        {value.map((v, idx) => {
          const value2: undefined | string = config.options?.find(
            (o) => o.value === v,
          )?.label;
          return <span key={idx}>{value2 || ""}</span>;
        })}
      </ul>
    );
  }

  if (Array.isArray(value)) {
    return (
      <ul>
        {value.map((v, idx) => (
          <li key={idx}>{v || ""}</li>
        ))}
      </ul>
    );
  } else if (
    typeof value === "object" &&
    value !== null &&
    "start" in value &&
    "end" in value
  ) {
    return (
      <span>
        {value.start} - {value.end}
      </span>
    );
  }
  if (
    typeof value === "object" &&
    value !== null &&
    "type" in value &&
    "value" in value
  ) {
    return (
      <span>
        {
          (
            value as {
              type: string;
              value: string;
            }
          ).value
        }{" "}
        (
        {(
          value as {
            type: string;
            value: string;
          }
        ).type.toUpperCase()}
        )
      </span>
    );
  } else {
    return <span>{value}</span>;
  }
};

const getAssigmentStatusLabel = (
  status: keyof typeof FORM_ASSIGNMENTS_STATUS,
) => {
  switch (status) {
    case FORM_ASSIGNMENTS_STATUS.SUBMITTED:
      return "Enviado";
    case FORM_ASSIGNMENTS_STATUS.COMPLETED:
      return "Completado";
    case FORM_ASSIGNMENTS_STATUS.IN_PROGRESS:
      return "En progreso";
    case FORM_ASSIGNMENTS_STATUS.CORRECTION:
      return "Revisión";
    case FORM_ASSIGNMENTS_STATUS.FILL_CORRECTION:
      return "Observado";
    case FORM_ASSIGNMENTS_STATUS.ASSIGNED:
      return "Pendiente";
    case FORM_ASSIGNMENTS_STATUS.FAILED_CORRECTION:
      return "Rechazado";
    default:
      return "-";
  }
};

const statusColor = (
  status: string,
): "info" | "success" | "warning" | "neutral" | "danger" => {
  switch (status) {
    case FORM_ASSIGNMENTS_STATUS.SUBMITTED:
      return "info";
    case FORM_ASSIGNMENTS_STATUS.COMPLETED:
      return "success";
    case FORM_ASSIGNMENTS_STATUS.IN_PROGRESS:
      return "info";
    case FORM_ASSIGNMENTS_STATUS.CORRECTION:
      return "info";
    case FORM_ASSIGNMENTS_STATUS.FILL_CORRECTION:
      return "warning";
    case FORM_ASSIGNMENTS_STATUS.ASSIGNED:
      return "neutral";
    case FORM_ASSIGNMENTS_STATUS.FAILED_CORRECTION:
      return "danger";
    default:
      return "neutral";
  }
};

const DatabaseFieldValue = ({
  value,
  table_name,
  table_source,
  column_name,
}: {
  value: string | number;
  table_name: string;
  column_name: string;
  table_source: string;
}) => {
  const { data } = useGetTableData(table_name!, table_source, 1, [
    {
      name: "id",
      value: value as string,
      operator: "EQUALS",
      type: "STRING",
    },
  ]);
  if (!data) return "Cargando...";

  const record = data?.data?.data?.[0];
  if (record) {
    return (
      <span className="text-body">
        {record[column_name as keyof typeof record] as string}
      </span>
    );
  }

  return <span className="text-body">{"—"}</span>;
};
